import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Paper,
  Slider,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffOutlinedIcon from "@mui/icons-material/SearchOffOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import HomeWorkOutlinedIcon from "@mui/icons-material/HomeWorkOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import PropertyCard from "./PropertyCard";
import PropertyForm from "./PropertyForm";
import PropertyPortfolioMap from "./PropertyPortfolioMap";
import PropertyProfileDialog from "./PropertyProfileDialog";
import { createProperty, listProperties } from "./propertyApi";
import { BRAND_BLUE, BRAND_GREEN } from "../../theme/colors";
import { PRIMARY_BUTTON_SX } from "../../theme/buttonstyles";

const DEFAULT_SQUARE_FOOTAGE_MAX = 1_000_000;
const SQUARE_FOOTAGE_STEP = 10_000;

const formatSquareFeet = (value) => `${Number(value).toLocaleString()} sq. ft.`;

export default function Properties() {
  const [activeTab, setActiveTab] = useState("properties");
  const [properties, setProperties] = useState([]);
  const [search, setSearch] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [propertyStatus, setPropertyStatus] = useState("");
  const [city, setCity] = useState("");
  const [squareFootageRange, setSquareFootageRange] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profileProperty, setProfileProperty] = useState(null);
  const location = useLocation();
  const [success, setSuccess] = useState(() => location.state?.message || "");
  const navigate = useNavigate();

  useEffect(() => {
    if (location.state?.message) {
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let active = true;
    listProperties()
      .then((records) => {
        if (active) setProperties(records);
      })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.detail || "Properties could not be loaded.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const filteredProperties = useMemo(() => {
    const term = search.trim().toLocaleLowerCase();
    const [minimum, maximum] = squareFootageRange || [null, null];
    const matches = properties.filter((property) => {
      const matchesSearch = !term || [
        property.name,
        property.streetAddress,
        property.city,
        property.state,
        property.zipCode,
        property.propertyType,
        property.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase()
        .includes(term);
      const squareFeet = Number(property.squareFeet || 0);
      return matchesSearch
        && (!propertyType || property.propertyType === propertyType)
        && (!propertyStatus || property.status === propertyStatus)
        && (!city || property.city === city)
        && (minimum == null || squareFeet >= minimum)
        && (maximum == null || squareFeet <= maximum);
    });
    return matches;
  }, [city, properties, propertyStatus, propertyType, search, squareFootageRange]);

  const squareFootageMaximum = useMemo(() => {
    const largestProperty = Math.max(0, ...properties.map((property) => Number(property.squareFeet) || 0));
    return Math.max(
      DEFAULT_SQUARE_FOOTAGE_MAX,
      Math.ceil(largestProperty / SQUARE_FOOTAGE_STEP) * SQUARE_FOOTAGE_STEP,
    );
  }, [properties]);

  const filterOptions = useMemo(() => {
    const unique = (field) => [...new Set(properties.map((property) => property[field]).filter(Boolean))]
      .sort((left, right) => left.localeCompare(right));
    return {
      propertyTypes: unique("propertyType"),
      statuses: unique("status").filter((status) => status !== "Sold"),
      cities: unique("city"),
    };
  }, [properties]);

  const hasFilters = Boolean(
    propertyType || propertyStatus || city || squareFootageRange,
  );

  const clearFilters = () => {
    setPropertyType("");
    setPropertyStatus("");
    setCity("");
    setSquareFootageRange(null);
  };

  const emptyState = !properties.length
    ? {
      Icon: ApartmentOutlinedIcon,
      title: "No properties yet",
      description: "Add your first property to start building and managing your portfolio.",
    }
    : search.trim()
      ? {
        Icon: SearchOffOutlinedIcon,
        title: "No search results",
        description: `We couldn't find a property matching “${search.trim()}”.`,
        resetLabel: "Clear search",
        onReset: () => setSearch(""),
      }
      : {
        Icon: FilterAltOffOutlinedIcon,
        title: "No properties match these filters",
        description: "Try clearing your filters, or add a new property to your portfolio.",
        resetLabel: "Clear filters",
        onReset: clearFilters,
      };

  const handleCreate = async (form) => {
    setSaving(true);
    setError("");
    try {
      const property = await createProperty(form);
      setProperties((current) => [property, ...current]);
      setSuccess("Property added successfully.");
      setActiveTab("properties");
    } catch (requestError) {
      setError(requestError?.response?.data?.detail || "Property could not be added.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: "100%", minWidth: 0, minHeight: "100vh", boxSizing: "border-box", bgcolor: "#f7f9fc", pb: 4 }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 1.75, mb: 3, bgcolor: "white", borderBottom: "1px solid #dce4ef", display: "flex", alignItems: { xs: "flex-start", sm: "center" }, justifyContent: "space-between", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 32, height: 32, borderRadius: 2, bgcolor: BRAND_BLUE, color: "white", display: "grid", placeItems: "center", flexShrink: 0 }}>
            <HomeWorkOutlinedIcon sx={{ fontSize: 19 }} />
          </Box>
          <Box>
            <Typography component="h1" sx={{ color: BRAND_BLUE, fontSize: 21, fontWeight: 800, lineHeight: 1.05 }}>Properties</Typography>
            <Typography sx={{ color: "#7b8aaa", fontSize: 12.5, mt: 0.35 }}>Manage your organization&apos;s property portfolio</Typography>
          </Box>
        </Box>
        {activeTab === "properties" && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setActiveTab("add")} sx={PRIMARY_BUTTON_SX}>
            Add property
          </Button>
        )}
      </Box>

      <Box sx={{ px: { xs: 2, md: 3 } }}>

      {error && <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>{error}</Alert>}

      {activeTab === "properties" && (
        <>
          <Paper elevation={0} sx={{ mb: 3, p: { xs: 2, md: 2.5 }, borderRadius: 2, border: "1px solid #dfe5ec", boxShadow: "0 4px 14px rgba(15,23,42,.055)" }}>
            <Box sx={{ mb: 2, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <FilterListIcon sx={{ color: BRAND_GREEN }} />
                <Typography sx={{ color: BRAND_BLUE, fontWeight: 800 }}>Search and filters</Typography>
              </Box>
              {hasFilters && <Button size="small" onClick={clearFilters} sx={{ textTransform: "none", fontWeight: 700 }}>Clear filters</Button>}
            </Box>
            <TextField
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by property name, address, type, or status"
              aria-label="Search properties"
              fullWidth
              sx={{ mb: 2, bgcolor: "white", "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
            />
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, minmax(0, 1fr))" }, gap: 1.5, alignItems: "start" }}>
              {[
                ["Property type", propertyType, setPropertyType, filterOptions.propertyTypes],
                ["Status", propertyStatus, setPropertyStatus, filterOptions.statuses],
                ["City", city, setCity, filterOptions.cities],
              ].map(([label, value, setter, options]) => (
                <TextField key={label} select label={label} value={value} onChange={(event) => setter(event.target.value)} size="small">
                  <MenuItem value="">All</MenuItem>
                  {options.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                </TextField>
              ))}
              <Box sx={{ gridColumn: { sm: "span 2" }, px: 1, minWidth: 0 }}>
                <Typography sx={{ color: "#526174", fontSize: 12, fontWeight: 700, mb: 0.25 }}>
                  Property size: {formatSquareFeet((squareFootageRange || [0, squareFootageMaximum])[0])} – {formatSquareFeet((squareFootageRange || [0, squareFootageMaximum])[1])}
                </Typography>
                <Slider
                  value={squareFootageRange || [0, squareFootageMaximum]}
                  onChange={(_, value) => setSquareFootageRange(value)}
                  min={0}
                  max={squareFootageMaximum}
                  step={SQUARE_FOOTAGE_STEP}
                  valueLabelDisplay="auto"
                  valueLabelFormat={formatSquareFeet}
                  getAriaLabel={(index) => index === 0 ? "Minimum property square footage" : "Maximum property square footage"}
                  sx={{ color: BRAND_GREEN, py: 1 }}
                />
              </Box>
            </Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${alpha(BRAND_BLUE, 0.08)}` }}>
              <PropertyPortfolioMap
                properties={properties}
                onSelect={(property) => navigate(`/properties/${property.id}`)}
              />
            </Box>
          </Paper>
          {!loading && (
            <Typography
              variant="body2"
              sx={{ mb: 1.5, color: "#667588", fontWeight: 700 }}
            >
              {properties.length.toLocaleString()} {properties.length === 1 ? "Property" : "Properties"}
              {(search || hasFilters) && filteredProperties.length !== properties.length
                ? ` · ${filteredProperties.length.toLocaleString()} matching`
                : ""}
            </Typography>
          )}

          {loading ? (
            <Box sx={{ py: 10, display: "flex", justifyContent: "center" }}><CircularProgress sx={{ color: BRAND_GREEN }} /></Box>
          ) : filteredProperties.length ? (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(3, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 2 }}>
              {filteredProperties.map((property) => (
                <PropertyCard key={property.id} property={property} onClick={() => navigate(`/properties/${property.id}`)} onPreview={setProfileProperty} />
              ))}
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                py: { xs: 7, sm: 9 },
                px: 3,
                textAlign: "center",
                borderRadius: 3,
                border: `1px dashed ${alpha(BRAND_BLUE, 0.22)}`,
                bgcolor: alpha(BRAND_BLUE, 0.015),
              }}
            >
              <Box
                sx={{
                  width: 72,
                  height: 72,
                  mx: "auto",
                  mb: 2,
                  display: "grid",
                  placeItems: "center",
                  borderRadius: "50%",
                  bgcolor: alpha(BRAND_GREEN, 0.1),
                }}
              >
                <emptyState.Icon sx={{ fontSize: 38, color: BRAND_GREEN }} />
              </Box>
              <Typography variant="h5" sx={{ color: BRAND_BLUE, fontWeight: 800 }}>
                {emptyState.title}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 1, mx: "auto", maxWidth: 520 }}>
                {emptyState.description}
              </Typography>
              <Box sx={{ mt: 3, display: "flex", justifyContent: "center", flexWrap: "wrap", gap: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setActiveTab("add")}
                  sx={PRIMARY_BUTTON_SX}
                >
                  Add property
                </Button>
                {emptyState.onReset && (
                  <Button variant="outlined" onClick={emptyState.onReset} sx={{ fontWeight: 700, textTransform: "none" }}>
                    {emptyState.resetLabel}
                  </Button>
                )}
              </Box>
            </Paper>
          )}
        </>
      )}

      {activeTab === "add" && (
        <PropertyForm
          title="Add a property"
          description="Enter the basic details for this property. You can update them later."
          submitLabel="Add property"
          saving={saving}
          onSubmit={handleCreate}
          onCancel={() => setActiveTab("properties")}
        />
      )}

      <Snackbar open={Boolean(success)} autoHideDuration={4000} onClose={() => setSuccess("")}>
        <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>
      </Snackbar>
      <PropertyProfileDialog
        property={profileProperty}
        onClose={() => setProfileProperty(null)}
        onOpenDetails={(property) => navigate(`/properties/${property.id}`)}
      />
      </Box>
    </Box>
  );
}
